import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Crown, ShieldAlert, Flame, MessageSquareReply, ChevronRight, Gift, Copy, TrendingUp, TrendingDown, Minus, Heart, MessageCircle, Stethoscope, Trophy, Shield, Megaphone, Brain, Rocket } from "lucide-react";
import Layout from "@/components/Layout";
import NpsPrompt from "@/components/NpsPrompt";
import { api, vibrate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/config";
import { listContainer, listItem } from "@/lib/motion";
import { SkeletonList } from "@/components/Skeleton";

const VLABEL = {
  red_flags: { label: "Red Flags", icon: ShieldAlert },
  rizz_score: { label: "Rizz Score", icon: Flame },
  reply_suggestions: { label: "Réponses", icon: MessageSquareReply },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [convos, setConvos] = useState([]);
  const [relations, setRelations] = useState([]);
  const [referral, setReferral] = useState(null);
  const [checkinsDue, setCheckinsDue] = useState(0);
  const [rankings, setRankings] = useState([]);
  const [relLoading, setRelLoading] = useState(true);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [isCreator, setIsCreator] = useState(false);

  useEffect(() => {
    // Creator signed up via the "Programme Créateurs" flow → send them to their space.
    if (localStorage.getItem("dmd_creator_intent")) {
      navigate("/createurs", { replace: true });
      return;
    }
    // Commercial logging in via /pro → send them to their space.
    if (localStorage.getItem("dmd_pro_intent")) {
      navigate("/pro", { replace: true });
      return;
    }
    // Commercials land on their /pro space by default (once per session, so they can still browse the app).
    if (user?.is_commercial && !sessionStorage.getItem("dmd_pro_seen")) {
      sessionStorage.setItem("dmd_pro_seen", "1");
      navigate("/pro", { replace: true });
      return;
    }
    api.get("/me/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/relationships").then((r) => setRelations(r.data)).catch(() => {}).finally(() => setRelLoading(false));
    api.get("/doctor/plans").then((r) => setCheckinsDue(r.data.checkins_due || 0)).catch(() => {});
    api.get("/profile/rankings/me").then((r) => setRankings(r.data.rankings || [])).catch(() => {});
    api.get("/ambassador/me").then((r) => setIsAmbassador(!!r.data.is_ambassador)).catch(() => {});
    api.get("/creators/me").then((r) => setIsCreator(!!r.data.is_creator)).catch(() => {});

    const promo = localStorage.getItem("dmd_promo");
    if (promo) {
      api.post("/ambassador/apply", { code: promo })
        .then((res) => { if (!res.data.already && res.data.ambassador_name) toast.success(`Code ${res.data.ambassador_name} appliqué 🎁 -50% le 1ᵉʳ mois`); })
        .catch(() => {})
        .finally(() => localStorage.removeItem("dmd_promo"));
    }
    const creatorCode = localStorage.getItem("dmd_creator");
    if (creatorCode) {
      api.post("/creators/apply", { code: creatorCode })
        .catch(() => {})
        .finally(() => localStorage.removeItem("dmd_creator"));
    }

    const pending = localStorage.getItem("dmd_ref");
    const loadReferral = () => api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
    if (pending) {
      api.post("/referral/apply", { code: pending })
        .then((res) => { if (!res.data.already) toast.success("Code parrainage appliqué 🎁"); })
        .catch(() => {})
        .finally(() => { localStorage.removeItem("dmd_ref"); loadReferral(); });
    } else {
      loadReferral();
    }

    const vip = localStorage.getItem("dmd_vip");
    if (vip) {
      api.post("/invites/claim", { code: vip })
        .then((res) => {
          if (!res.data.already) {
            toast.success("🎁 Accès VIP activé, bienvenue !");
            api.get("/me/usage").then((r) => setUsage(r.data)).catch(() => {});
          }
        })
        .catch(() => {})
        .finally(() => localStorage.removeItem("dmd_vip"));
    }
  }, []);

  const inviteUrl = referral ? `${window.location.origin}/?ref=${referral.code}` : "";

  const shareInvite = async () => {
    vibrate();
    const text = `Analyse tes DM sur ${APP_NAME} 👀 utilise mon code ${referral.code} — 1 semaine Pro dès 3 ami·e·s, 1 mois offert dès 5 : ${inviteUrl}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: APP_NAME, text, url: inviteUrl });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Lien d'invitation copié 🎁");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        await navigator.clipboard.writeText(text);
        toast.success("Lien copié 🎁");
      }
    }
  };

  const isPaid = usage?.is_paid;

  const quotaColor = (used, limit) => {
    if (limit == null) return "#10b981";
    const pct = limit ? used / limit : 0;
    if (pct >= 1) return "#ef4444";
    if (pct > 0.5) return "#f59e0b";
    return "#10b981";
  };

  const QuotaBadge = ({ label, used, limit, testid }) => {
    const unlimited = limit == null;
    const color = quotaColor(used, limit);
    const pct = unlimited ? 100 : Math.min(100, limit ? (used / limit) * 100 : 0);
    const full = !unlimited && used >= limit;
    return (
      <div data-testid={testid}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-300">{label}</span>
          <span className="text-sm font-bold" style={{ color }}>
            {unlimited ? "illimité" : `${used}/${limit}`}
          </span>
        </div>
        <div className="h-2 rounded-full bg-zinc-800 mt-2 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
        </div>
        {full && (
          <button
            data-testid={`${testid}-upgrade`}
            onClick={() => { vibrate(); navigate("/pricing?from=quota"); }}
            className="mt-2 text-xs font-semibold text-pink-400 flex items-center gap-1 hover:text-pink-300"
          >
            Débloquer plus <ChevronRight size={13} />
          </button>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <NpsPrompt trigger="dashboard" />
      <div className="space-y-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Salut {user?.name?.split(" ")[0] || "toi"} 👋</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2 leading-tight">
            Ton <span className="text-gradient">Docteur Love</span> veille sur tes relations
          </h1>
          {user?.is_admin && (
            <button
              data-testid="dashboard-admin-link"
              onClick={() => navigate("/admin")}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold px-3 py-1.5 hover:bg-emerald-500/20 transition-colors"
            >
              <Shield size={13} /> Console admin
            </button>
          )}
          {isAmbassador && (
            <button
              data-testid="dashboard-ambassador-link"
              onClick={() => navigate("/ambassadeur")}
              className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold px-3 py-1.5 hover:bg-pink-500/20 transition-colors"
            >
              <Megaphone size={13} /> Espace Ambassadrice
            </button>
          )}
          <button
            data-testid="dashboard-quiz-link"
            onClick={() => navigate("/quiz")}
            className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs font-bold px-3 py-1.5 hover:bg-purple-500/20 transition-colors"
          >
            <Brain size={13} /> Test d'attachement
          </button>
          <button
            data-testid="dashboard-creator-link"
            onClick={() => navigate("/createurs")}
            className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-300 text-xs font-bold px-3 py-1.5 hover:bg-pink-500/20 transition-colors"
          >
            <Rocket size={13} /> {isCreator ? "Espace Créateur" : "Devenir créateur · gagne 50%"}
          </button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-3xl p-5 space-y-4 shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
        >
          {isPaid ? (
            <div className="flex items-center gap-3">
              <Crown className="text-amber-400" size={22} />
              <div>
                <p className="font-semibold text-sm">Premium actif</p>
                <p className="text-xs text-zinc-500">Analyses illimitées · plan {usage?.plan}</p>
              </div>
            </div>
          ) : (
            usage && (
              <QuotaBadge
                label="Analyses gratuites"
                used={usage.free_analyses_used}
                limit={usage.free_analyses_limit}
                testid="usage-analyses"
              />
            )
          )}
          {usage && (
            <QuotaBadge
              label="Relations suivies"
              used={usage.relationships_used}
              limit={usage.relationships_limit}
              testid="usage-relations"
            />
          )}
          {!isPaid && (
            <button
              data-testid="go-premium"
              onClick={() => { vibrate(); navigate("/pricing"); }}
              className="text-xs text-pink-400 font-semibold flex items-center gap-1"
            >
              Passer Premium <ChevronRight size={14} />
            </button>
          )}
        </motion.div>

        <button
          data-testid="new-analysis-cta"
          onClick={() => { vibrate(); navigate("/import"); }}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-rose-600 to-purple-600 text-white font-semibold py-4 active:scale-[0.98] transition-transform shadow-[0_0_28px_rgba(225,29,72,0.35)] hover:shadow-[0_0_38px_rgba(225,29,72,0.5)]"
        >
          <Sparkles size={18} /> Nouvelle analyse
        </button>

        <button
          data-testid="whatsapp-monitor-cta"
          onClick={() => { vibrate(); navigate("/whatsapp"); }}
          className="w-full flex items-center justify-between rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 transition-colors p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400">
              <MessageCircle size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold flex items-center gap-2">
                Suivi WhatsApp 24/7
                <span className="text-[9px] font-bold uppercase tracking-wider text-pink-400 bg-pink-500/10 border border-pink-500/20 rounded-full px-1.5 py-0.5">Pro</span>
              </p>
              <p className="text-xs text-zinc-500">Le Doc veille sur ta relation en continu</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-zinc-500" />
        </button>

        <button
          data-testid="doctor-cta"
          onClick={() => { vibrate(); navigate("/doctor"); }}
          className="w-full flex items-center justify-between rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 transition-colors p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-500/15 flex items-center justify-center text-pink-400">
              <Stethoscope size={20} />
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold flex items-center gap-2">
                Docteur Love
                <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-1.5 py-0.5">Docteur</span>
              </p>
              <p className="text-xs text-zinc-500">Consultations vocales & plans d'action</p>
            </div>
          </div>
          {checkinsDue > 0 ? (
            <span className="text-[10px] font-bold text-white bg-amber-500 rounded-full px-2 py-1">{checkinsDue} check-in</span>
          ) : (
            <ChevronRight size={18} className="text-zinc-500" />
          )}
        </button>

        {referral && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 p-5"
            data-testid="referral-card"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Gift className="text-pink-400" size={18} />
                <p className="font-semibold text-sm">Parraine & gagne du Pro</p>
              </div>
              {referral.reward_active && (
                <span
                  data-testid="referral-reward-badge"
                  className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-1"
                >
                  {(referral.reward_tier || "pro").toUpperCase()} offert · J-{referral.reward_days_left}
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400">
              Chaque ami·e qui crée un compte <span className="text-zinc-300">et fait 1 analyse</span> te rapproche d'un accès Pro offert.
            </p>

            {/* Progression vers le prochain palier */}
            {(() => {
              const count = referral.referral_count || 0;
              const target = referral.next_milestone_at || 3;
              const pct = Math.min(100, target ? (count / target) * 100 : 0);
              return (
                <div className="mt-4" data-testid="referral-progress">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-zinc-400">
                      <span className="text-pink-400 font-bold">{count}</span>/{target} ami·e·s
                    </span>
                    <span className="text-zinc-300 font-semibold">→ {referral.next_milestone_reward}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Paliers */}
            <div className="mt-3 flex gap-2">
              {[
                { at: 3, label: "1 semaine Pro" },
                { at: 5, label: "1 mois offert" },
              ].map((m) => {
                const done = (referral.referral_count || 0) >= m.at;
                return (
                  <div
                    key={m.at}
                    className={`flex-1 rounded-xl border px-3 py-2 text-center ${done ? "border-emerald-500/40 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950"}`}
                  >
                    <p className={`text-sm font-bold ${done ? "text-emerald-400" : "text-zinc-300"}`}>
                      {m.at} ami·e·s {done && "✓"}
                    </p>
                    <p className="text-[11px] text-zinc-500">{m.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                data-testid="referral-code"
                onClick={() => { vibrate(); navigator.clipboard.writeText(referral.code); toast.success("Code copié"); }}
                className="flex-1 flex items-center justify-between rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-3"
              >
                <span className="font-mono font-bold tracking-widest text-pink-400">{referral.code}</span>
                <Copy size={14} className="text-zinc-500" />
              </button>
              <button
                data-testid="referral-share"
                onClick={shareInvite}
                className="rounded-xl bg-pink-500 text-white font-bold px-4 py-3 hover:bg-pink-600 transition-colors active:scale-95"
              >
                Inviter
              </button>
            </div>
          </motion.div>
        )}

        {rankings.length > 0 && (
          <div data-testid="alignment-rankings">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={15} className="text-amber-400" />
              <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Ton classement · alignement</h2>
            </div>
            <motion.div variants={listContainer} initial="hidden" animate="show" className="space-y-2">
              {rankings.map((r, i) => {
                const medal = ["🥇", "🥈", "🥉"][i] || `#${i + 1}`;
                const sc = r.score >= 70 ? "#10b981" : r.score >= 45 ? "#f59e0b" : "#ef4444";
                return (
                  <motion.button
                    variants={listItem}
                    key={r.relationship_id}
                    data-testid={`ranking-${r.relationship_id}`}
                    onClick={() => { vibrate(); navigate(`/profile/${r.relationship_id}`); }}
                    className="w-full flex items-center gap-3 rounded-2xl glass-card p-4 text-left hover:border-purple-400/40 transition-colors active:scale-[0.99]"
                  >
                    <span className="text-lg w-7 text-center shrink-0">{medal}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate">{r.contact_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{r.verdict}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="font-display text-xl font-black" style={{ color: sc }}>{r.score}<span className="text-xs text-zinc-600">%</span></span>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold">
              Mes relations suivies
            </h2>
            <button
              data-testid="history-see-all"
              onClick={() => { vibrate(); navigate("/history"); }}
              className="text-xs text-pink-400 font-semibold hover:text-pink-300"
            >
              Historique
            </button>
          </div>
          {relLoading ? (
            <SkeletonList count={3} testid="relationships-skeleton" />
          ) : relations.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center">
              <Heart className="mx-auto text-zinc-600 mb-2" size={24} />
              <p className="text-sm text-zinc-500">Aucune relation suivie.</p>
              <p className="text-xs text-zinc-600 mt-1">Analyse une conv pour commencer le suivi 👆</p>
            </div>
          ) : (
            <motion.div variants={listContainer} initial="hidden" animate="show" className="space-y-3" data-testid="relationships-list">
              {relations.map((r) => {
                const hc = r.health_score == null ? "#71717a" : r.health_score < 40 ? "#ef4444" : r.health_score < 70 ? "#f59e0b" : "#10b981";
                const TrIcon = r.trend === "up" ? TrendingUp : r.trend === "down" ? TrendingDown : Minus;
                const trC = r.trend === "up" ? "text-emerald-400" : r.trend === "down" ? "text-red-400" : "text-zinc-500";
                return (
                  <motion.button
                    variants={listItem}
                    key={r.relationship_id}
                    data-testid={`relationship-${r.relationship_id}`}
                    onClick={() => { vibrate(); navigate(`/relationship/${r.relationship_id}`); }}
                    className="w-full flex items-center gap-3 rounded-2xl glass-card p-4 text-left hover:border-white/20 transition-colors relative active:scale-[0.99]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-display font-black shrink-0">
                      {(r.contact_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm truncate flex items-center gap-2">
                        {r.contact_name}
                        {r.unread_alerts > 0 && (
                          <span className="text-[9px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5">{r.unread_alerts}</span>
                        )}
                      </p>
                      <p className={`text-xs flex items-center gap-1 ${trC}`}>
                        <TrIcon size={12} /> {r.analyses_count} analyse{r.analyses_count > 1 ? "s" : ""}
                      </p>
                      {r.profile_needs_update && (
                        <span data-testid={`profile-update-badge-${r.relationship_id}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-purple-300 bg-purple-500/10 border border-purple-500/20 rounded-full px-2 py-0.5">
                          <TrendingUp size={10} /> Profil à mettre à jour
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="font-display text-xl font-black" style={{ color: hc }}>{r.health_score ?? "—"}</span>
                      <p className="text-[9px] uppercase tracking-wider text-zinc-600">santé</p>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
}
